import {
  HttpError,
  type GetListParams,
  type QueryFunctionContext,
} from "react-admin";

import { createApiClient } from "@/api";
import { HTTP_STATUS } from "@/constants/http";
import { UserRole } from "@/constants/roles";
import { type DataProviderSchema } from "@/utils";
import { getLocalFilteredSortedList } from "@/utils/listParams";

import { OPTION_ALL_VALUE } from "../components/constants";

import { type UsersListFilter } from "./types";
import { getListQueryData } from "./utils";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/main/users";

const fetchListData = async ({
  pagination,
  filter,
}: GetListParams & QueryFunctionContext) => {
  const typedFilter = filter as UsersListFilter | undefined;
  const rawActive = typedFilter?.active;
  const active: boolean | undefined =
    rawActive === OPTION_ALL_VALUE || typeof rawActive !== "boolean"
      ? undefined
      : rawActive;
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: true,
        limit: pagination?.perPage,
        page: pagination?.page,
        active,
      },
    },
  });

  return {
    data: response.data?.["hydra:member"] ?? [],
    total: response.data?.["hydra:totalItems"] ?? 0,
  };
};

export const usersSchema: DataProviderSchema<"User", "jsonld"> = {
  list: getLocalFilteredSortedList(fetchListData, getListQueryData),
  detail: async ({ id }) => {
    const { data } = await apiClient.GET(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
    });

    if (!data) throw new Error("User not found");

    return { data };
  },
  create: async ({
    data: { username, email, groups = [], roles = [], plainPassword },
  }) => {
    const rolesToSubmit = (roles as UserRole[]).includes(UserRole.USER)
      ? roles
      : [...roles, UserRole.USER];

    const response = await apiClient.POST(BASE_URL, {
      body: {
        username,
        email,
        roles: rolesToSubmit,
        groups,
        plainPassword,
      },
    });

    if (!response.data)
      throw new HttpError(
        "Something went wrong!",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );

    return {
      data: response.data,
    };
  },
  edit: async ({
    id,
    data: { username, email, groups = [], roles, plainPassword, active },
  }) => {
    const typedGroups: Array<{ "@id": string } | string> = groups;
    const firstGroup = typedGroups[0];
    const isObjectFormat =
      typeof firstGroup === "object" && "@id" in firstGroup;
    const formattedGroups = isObjectFormat
      ? typedGroups
          .filter(
            (group): group is { "@id": string } =>
              typeof group === "object" && "@id" in group,
          )
          .map((group) => group["@id"])
      : typedGroups.filter(
          (group): group is string => typeof group === "string",
        );
    const typedRoles = roles as UserRole[] | undefined;
    const rolesToSubmit = typedRoles
      ? typedRoles.includes(UserRole.USER)
        ? typedRoles
        : [...typedRoles, UserRole.USER]
      : undefined;
    const response = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        username,
        email,
        groups: formattedGroups,
        roles: rolesToSubmit,
        plainPassword,
        active,
      },
    });

    if (!response.data)
      throw new HttpError(
        "Something went wrong!",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );

    return {
      data: response.data,
    };
  },
};
